import { createPortal } from 'react-dom';
import { generateInvoice } from './generateInvoice';

export default function HistoryModal({ user, onClose }) {
  if (!user) return null;
  const history = [...(user.serviceHistory || [])].sort((a, b) => new Date(b.date) - new Date(a.date));
  const totalSpent = history.reduce((sum, item) => sum + (Number(item.billAmount) || 0), 0);

  return createPortal(
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm grid place-items-center z-[9999] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-2xl max-h-[88vh] flex flex-col shadow-2xl border border-slate-200 rounded overflow-hidden animate-rise"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="bg-primary px-6 py-5 flex items-start justify-between gap-4 shrink-0">
          <div>
            <span className="inline-block text-[9px] font-extrabold tracking-widest uppercase text-white/60 mb-1">
              Service Visit History
            </span>
            <h2 className="font-heading text-xl font-bold text-white m-0 leading-tight capitalize">
              {String(user.firstName || '').toLowerCase()} {String(user.lastName || '').toLowerCase()}
            </h2>
            <div className="flex items-center gap-3 mt-1.5 flex-wrap">
              <span className="text-[11px] text-white/70 font-medium flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                {user.contact}
              </span>
              <span className="text-white/40 text-xs">·</span>
              <span className="text-[11px] text-white/70 font-medium flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {user.email}
              </span>
            </div>
          </div>
          <button
            className="w-8 h-8 rounded bg-white/15 hover:bg-white/25 text-white grid place-items-center transition-colors cursor-pointer shrink-0 border border-white/20"
            onClick={onClose}
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Summary Bar ── */}
        <div className={`grid border-b border-slate-100 shrink-0 ${history.length > 1 ? 'grid-cols-3' : 'grid-cols-2'}`}>
          <div className="px-6 py-3 border-r border-slate-100 flex flex-col justify-center">
            <p className="text-[9px] font-extrabold text-slate-400 uppercase tracking-wider">Total Visits</p>
            <p className="text-xl font-bold text-primary mt-0.5">{history.length}</p>
          </div>
          <div className={`px-6 py-3 flex flex-col justify-center ${history.length > 1 ? 'border-r border-slate-100' : ''}`}>
            <p className="text-[9px] font-extrabold text-slate-400 uppercase tracking-wider">Total Revenue</p>
            <p className="text-xl font-bold text-cta mt-0.5">₹{totalSpent.toLocaleString()}</p>
          </div>
          {history.length > 1 && (
            <div className="px-6 py-3 flex flex-col justify-center">
              <p className="text-[9px] font-extrabold text-slate-400 uppercase tracking-wider">Master Record</p>
              <button
                onClick={() => generateInvoice(user, history, "ALL")}
                className="mt-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded bg-cta hover:bg-cta-hover text-white text-[11px] font-bold transition-colors shadow-sm cursor-pointer w-fit"
                title="Download Master PDF for all visits"
              >
                <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 512 512">
                  <path d="M64 464H96v48H64c-35.3 0-64-28.7-64-64V64C0 28.7 28.7 0 64 0H229.5c17 0 33.3 6.7 45.3 18.7l90.5 90.5c12 12 18.7 28.3 18.7 45.3V288H336V160H256c-17.7 0-32-14.3-32-32V48H64c-8.8 0-16 7.2-16 16V448c0 8.8 7.2 16 16 16zM176 352h32c30.9 0 56 25.1 56 56s-25.1 56-56 56H192v32c0 8.8-7.2 16-16 16s-16-7.2-16-16V316c0-6.1 3.4-11.6 8.8-14.3s11.9-2.1 16.8 1.5L208 320V352zm32 80c13.3 0 24-10.7 24-24s-10.7-24-24-24H192v48h16zm96-80h32c26.5 0 48 21.5 48 48v64c0 26.5-21.5 48-48 48H304c-8.8 0-16-7.2-16-16V316c0-6.1 3.4-11.6 8.8-14.3s11.9-2.1 16.8 1.5l22.4 16.8v32zm32 128c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H320v96h16zm80-112c0-8.8 7.2-16 16-16h48c8.8 0 16 7.2 16 16s-7.2 16-16 16H448v32h32c8.8 0 16 7.2 16 16s-7.2 16-16 16H448v48c0 8.8-7.2 16-16 16s-16-7.2-16-16V368z" />
                </svg>
                Master Invoice
              </button>
            </div>
          )}
        </div>

        {/* ── Table ── */}
        <div className="overflow-y-auto flex-1">
          <table className="w-full border-collapse text-left text-xs md:text-sm">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-50 text-slate-500 text-[10px] font-extrabold uppercase tracking-wider border-b border-slate-200">
                <th className="py-3 px-6">Visit Date</th>
                <th className="py-3 px-6">Treated Services</th>
                <th className="py-3 px-6 text-right">Amount</th>
                <th className="py-3 px-6 text-right">Receipt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {history.map((entry, index) => (
                <tr key={entry._id || entry.date || index} className="hover:bg-slate-50/80 transition-colors group">
                  <td className="py-4 px-6 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded bg-primary/10 border border-primary/15 grid place-items-center shrink-0">
                        <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <span className="block font-bold text-slate-800 text-xs">
                          {new Date(entry.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        <span className="block text-[10px] text-slate-400 font-medium">Visit #{history.length - index}</span>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex flex-wrap gap-1.5">
                      {(entry.services || []).map((s) => (
                        <span
                          key={s}
                          className="bg-accent/10 text-accent-muted text-[10px] font-bold px-2 py-0.5 rounded border border-accent/20"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-4 px-6 text-right">
                    <span className="font-extrabold text-cta text-sm block mb-1.5">
                      ₹{Number(entry.billAmount || 0).toLocaleString()}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-right">
                    <button
                      onClick={() => generateInvoice(user, entry, history.length - index)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 text-[10px] font-bold transition-colors border border-slate-200 shadow-xs cursor-pointer group-hover:border-slate-300"
                      title="Download PDF Receipt"
                    >
                      <svg className="w-3.5 h-3.5 text-red-500" fill="currentColor" viewBox="0 0 512 512">
                        <path d="M64 464H96v48H64c-35.3 0-64-28.7-64-64V64C0 28.7 28.7 0 64 0H229.5c17 0 33.3 6.7 45.3 18.7l90.5 90.5c12 12 18.7 28.3 18.7 45.3V288H336V160H256c-17.7 0-32-14.3-32-32V48H64c-8.8 0-16 7.2-16 16V448c0 8.8 7.2 16 16 16zM176 352h32c30.9 0 56 25.1 56 56s-25.1 56-56 56H192v32c0 8.8-7.2 16-16 16s-16-7.2-16-16V316c0-6.1 3.4-11.6 8.8-14.3s11.9-2.1 16.8 1.5L208 320V352zm32 80c13.3 0 24-10.7 24-24s-10.7-24-24-24H192v48h16zm96-80h32c26.5 0 48 21.5 48 48v64c0 26.5-21.5 48-48 48H304c-8.8 0-16-7.2-16-16V316c0-6.1 3.4-11.6 8.8-14.3s11.9-2.1 16.8 1.5l22.4 16.8v32zm32 128c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H320v96h16zm80-112c0-8.8 7.2-16 16-16h48c8.8 0 16 7.2 16 16s-7.2 16-16 16H448v32h32c8.8 0 16 7.2 16 16s-7.2 16-16 16H448v48c0 8.8-7.2 16-16 16s-16-7.2-16-16V368z" />
                      </svg>
                      PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Footer ── */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/60 shrink-0 flex justify-end">
          <button
            className="bg-primary hover:bg-primary-dark text-white font-bold text-xs px-5 py-2.5 rounded transition-colors cursor-pointer border-0 shadow-sm"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
